// ─────────────────────────────────────────────────────────────────────────────
// LocationEmailsEffects
// ─────────────────────────────────────────────────────────────────────────────
// NgRx effects for the locationEmails slice.
// Responsibilities:
//   - Load per-location email registry from /v1/location-emails
//   - Create / Update / Delete with typed error handling (409 duplicate, 404 not-found)
//   - Map HTTP 404 on Update/Delete to a friendly "row no longer exists"
//     toast + re-dispatch LoadLocationEmails so the UI re-syncs with reality
//     (ERP rows return 404 by design to prevent ID-spraying)
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { CommonService } from 'src/app/shared/common.service';
import { LocationEmailService } from '../services/location-email.service';
import { ILocationEmail, IStructuredApiError, STRUCTURED_ERROR_CODES } from '../models/location-email.models';
import * as locationEmailsActions from './location-emails.actions';

@Injectable()
export class LocationEmailsEffects {
  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private locationEmailService: LocationEmailService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  // ── Load location emails ─────────────────────────────────────────────────

  LoadLocationEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationEmailsActions.LoadLocationEmails),
      switchMap((action) =>
        this.locationEmailService.list(action.locationId).pipe(
          map((response: APIResponse<ILocationEmail>) =>
            locationEmailsActions.LoadLocationEmailsSuccess({
              locationId: action.locationId,
              response,
            }),
          ),
          catchError((err: IStructuredApiError) => of(locationEmailsActions.LoadLocationEmailsError({ error: err?.message || 'Load failed' }))),
        ),
      ),
    ),
  );

  // ── Load company-wide location emails ────────────────────────────────────

  LoadCompanyLocationEmails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationEmailsActions.LoadCompanyLocationEmails),
      switchMap((action) =>
        this.locationEmailService.listForCompany(action.customerId).pipe(
          map((response: APIResponse<ILocationEmail>) =>
            locationEmailsActions.LoadCompanyLocationEmailsSuccess({
              customerId: action.customerId,
              response,
            }),
          ),
          catchError((err: IStructuredApiError) =>
            of(locationEmailsActions.LoadCompanyLocationEmailsError({ error: err?.message || 'Load failed' })),
          ),
        ),
      ),
    ),
  );

  // ── Create location email ────────────────────────────────────────────────

  CreateLocationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationEmailsActions.CreateLocationEmail),
      mergeMap((action) =>
        this.locationEmailService.create(action.payload).pipe(
          map((response: APIResponse<ILocationEmail>) => {
            const email = response.object ?? response.data[0];
            return locationEmailsActions.CreateLocationEmailSuccess({ email, customerId: action.customerId });
          }),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('customers.contacts_tab.email_create_success'), 'SUCCESS');
          }),
          catchError((error: IStructuredApiError) => {
            this.commonService.showToast(this.resolveToastMessage(error), 'ERROR');
            return of(locationEmailsActions.CreateLocationEmailError({ error }));
          }),
        ),
      ),
    ),
  );

  // ── Update location email ────────────────────────────────────────────────

  UpdateLocationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationEmailsActions.UpdateLocationEmail),
      mergeMap((action) =>
        this.locationEmailService.update(action.id, action.payload).pipe(
          map((response: APIResponse<ILocationEmail>) => {
            const email = response.object ?? response.data[0];
            return locationEmailsActions.UpdateLocationEmailSuccess({ email });
          }),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('customers.contacts_tab.email_update_success'), 'SUCCESS');
          }),
          catchError((error: IStructuredApiError) => {
            this.commonService.showToast(this.resolveToastMessage(error), 'ERROR');
            if (error?.code === STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND) {
              this.store.dispatch(locationEmailsActions.LoadCompanyLocationEmails({ customerId: action.customerId }));
            }
            return of(locationEmailsActions.UpdateLocationEmailError({ error, locationId: action.locationId }));
          }),
        ),
      ),
    ),
  );

  // ── Delete location email ────────────────────────────────────────────────

  DeleteLocationEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(locationEmailsActions.DeleteLocationEmail),
      mergeMap((action) =>
        this.locationEmailService.remove(action.id).pipe(
          map(() =>
            locationEmailsActions.DeleteLocationEmailSuccess({
              id: action.id,
              locationId: action.locationId,
            }),
          ),
          tap(() => {
            this.commonService.showToast(this.translateService.instant('customers.contacts_tab.email_delete_success'), 'SUCCESS');
          }),
          catchError((error: IStructuredApiError) => {
            this.commonService.showToast(this.resolveToastMessage(error), 'ERROR');
            if (error?.code === STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND) {
              this.store.dispatch(locationEmailsActions.LoadCompanyLocationEmails({ customerId: action.customerId }));
            }
            return of(locationEmailsActions.DeleteLocationEmailError({ error, locationId: action.locationId }));
          }),
        ),
      ),
    ),
  );

  // ── Helpers ──────────────────────────────────────────────────────────────

  private resolveToastMessage(error: IStructuredApiError): string {
    if (error?.code === STRUCTURED_ERROR_CODES.EMAIL_NOT_FOUND) {
      return this.translateService.instant('customers.contacts_tab.email_not_found_error');
    }
    if (error?.code === STRUCTURED_ERROR_CODES.DUPLICATE) {
      return this.translateService.instant('customers.contacts_tab.email_duplicate_error');
    }
    return error?.message || this.translateService.instant('customers.contacts_tab.email_server_error');
  }
}
